import type { AppDispatch } from "@/store";
import { openModal, closeModal, setPollingError, setPollingSuccess } from "@/store/slices/ui/uiSlice";
import { setEditSixId } from "@/store/slices/createSix/createSixSlice";
import { fetchParticipantsThunk } from "@/store/thunks/sixDetail/fetchParticipantsThunk";
import { openManageAccessThunk } from "@/store/thunks/manageAccess/openManageAccessThunk";
import { showErrorToast } from "@/utils/toast.utils";

const MEMBERSHIP_LOAD_FAILED_MESSAGE = 'Failed to load current SIX membership';

/**
 * Opens the 'editUser' modal.
 */
export const showEditUserModal = () => (dispatch: AppDispatch) => {
  dispatch(openModal({ modal: 'editUser', modalProps: {} }));
};

/**
 * Opens the 'inviteExternal' modal.
 */
export const showInviteExternalModal = () => (dispatch: AppDispatch) => {
  dispatch(openModal({ modal: 'inviteExternal', modalProps: {} }));
};

/**
 * Opens the 'createSix' modal.
 */
export const showCreateSixModal = () => (dispatch: AppDispatch) => {
  dispatch(openModal({ modal: 'createSix', modalProps: {} }));
};

/**
 * Closes any open modal.
 */
export const hideModal = () => (dispatch: AppDispatch) => {
  dispatch(closeModal());
};

/**
 * Shows a polling error notification in the global polling modal.
 */
export const showPollingError =
  (title: string, message: string, details?: Record<string, unknown> | null) =>
  (dispatch: AppDispatch) => {
    dispatch(setPollingError({ title, message, details: details ?? null }));
  };

/**
 * Shows a polling success notification in the global polling modal.
 */
export const showPollingSuccess =
  (title: string, message: string, resourceUrl?: string | null, details?: Record<string, unknown> | null) =>
  (dispatch: AppDispatch) => {
    dispatch(setPollingSuccess({ title, message, resourceUrl: resourceUrl ?? null }));
  };

/**
 * Opens the 'fileUpload' modal (FileUploadDialog).
 */
export const showFileUploadModal = () => (dispatch: AppDispatch) => {
  dispatch(openModal({ modal: 'fileUpload', modalProps: {} }));
};

/**
 * Opens the 'fileDetail' modal (FileDetailsModal).
 */
export const showFileDetailModal = () => (dispatch: AppDispatch) => {
  dispatch(openModal({ modal: 'fileDetail', modalProps: {} }));
};

/**
 * Fetches fresh participants for the SIX, hydrates the manage-access draft from
 * that fresh payload, and only then opens the 'manageAccess' modal.
 *
 * If the participant fetch fails, the modal does not open and the user is shown
 * a single error toast.
 */
export const showManageAccessModal = (sixId: string) => async(dispatch: AppDispatch) => {
  try {
    const participants = await dispatch(fetchParticipantsThunk(sixId)).unwrap();
    await dispatch(openManageAccessThunk({ sixId, participants })).unwrap();
    dispatch(openModal({ modal: 'manageAccess', modalProps: {} }));
  } catch {
    showErrorToast(MEMBERSHIP_LOAD_FAILED_MESSAGE);
  }
};

/**
 * Opens the 'createFolder' modal within a SIX detail view.
 */
export const showCreateFolderModal = () => (dispatch: AppDispatch) => {
  dispatch(openModal({ modal: 'createFolder', modalProps: {} }));
};

/**
 * Fetches fresh participants for the SIX, then opens the 'createSix' modal in edit mode.
 *
 * `setEditSixId` is set only on the success path so that a failed fetch does not
 * leak edit-mode state into the next time someone opens the create-six modal.
 */
export const showEditSixModal = (sixId: string) => async(dispatch: AppDispatch) => {
  try {
    await dispatch(fetchParticipantsThunk(sixId)).unwrap();
    dispatch(setEditSixId(sixId));
    dispatch(openModal({ modal: 'createSix', modalProps: {} }));
  } catch {
    showErrorToast(MEMBERSHIP_LOAD_FAILED_MESSAGE);
  }
};
